import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader, StatusBadge } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function AdminVerifications() {
  useSEO("Admin · Verifications");
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get("/admin/verifications").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const review = async (status) => {
    try {
      const { data } = await api.post(`/admin/verifications/${sel.id}/review`, { status, admin_note: note });
      if (status === "approved" && data.vip_applied) {
        toast.success(data.vip_applied.lifetime ? "Approved — Lifetime VIP activated" : "Approved — VIP benefit activated");
      } else {
        toast.success(`Verification ${status}`);
      }
      setSel(null); setNote(""); load();
    } catch { toast.error("Failed"); }
  };

  const filtered = rows.filter((r) => filter === "all" || r.status === filter);

  return (
    <div>
      <AdminHeader title="Broker Verifications" subtitle="Review and approve member account verifications" />
      <div className="flex gap-2 mb-4">
        {["all", "pending", "approved", "rejected"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`verif-filter-${f}`} className={`px-4 py-2 rounded-full text-sm capitalize border ${filter === f ? "border-brand-cyan text-brand-cyan bg-brand-cyan/10" : "border-white/10 text-slate-400"}`}>{f}</button>
        ))}
      </div>
      <div className="card-surface rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-white/10"><th className="p-4">Member</th><th className="p-4">Broker</th><th className="p-4">UID</th><th className="p-4">Status</th><th className="p-4">Actions</th></tr></thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-slate-500">No verifications.</td></tr>
              : filtered.map((r) => (
                <tr key={r.id} className="border-b border-white/5" data-testid={`verif-row-${r.id}`}>
                  <td className="p-4 text-white">{r.user_name}<br /><span className="text-xs text-slate-500">{r.user_email}</span></td>
                  <td className="p-4 text-slate-400">{r.broker_name}</td>
                  <td className="p-4 font-mono text-slate-400">{r.account_uid}</td>
                  <td className="p-4"><StatusBadge status={r.status} /></td>
                  <td className="p-4"><Button size="sm" variant="outline" className="h-8 border-white/15 text-white" onClick={() => { setSel(r); setNote(r.admin_note || ""); }} data-testid={`review-${r.id}`}>Review</Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="bg-brand-surface border-white/10">
          <DialogHeader><DialogTitle className="text-white">Review Verification</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-500">Member</span><p className="text-white">{sel.user_name}</p></div>
                <div><span className="text-slate-500">Broker</span><p className="text-white">{sel.broker_name}</p></div>
                <div><span className="text-slate-500">Account UID</span><p className="text-white font-mono">{sel.account_uid}</p></div>
                <div><span className="text-slate-500">Full Name</span><p className="text-white">{sel.full_name}</p></div>
                <div><span className="text-slate-500">Account Email</span><p className="text-white">{sel.account_email || "—"}</p></div>
              </div>
              {sel.notes && <div><span className="text-slate-500">Member notes</span><p className="text-slate-300 bg-white/5 rounded-lg p-2">{sel.notes}</p></div>}
              <p className="text-xs text-slate-500">Approving a verification for a broker with a VIP-granting benefit will automatically activate the member's VIP — no separate approval needed.</p>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Admin note (optional)" data-testid="review-note" className="bg-white/5 border-white/10" />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={() => review("rejected")} data-testid="review-reject">Reject</Button>
            <Button className="bg-brand-green/20 text-brand-green hover:bg-brand-green/30" onClick={() => review("approved")} data-testid="review-approve">Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
